import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { commafy, decommafy, swal, toEnglishDigits, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const ConsultantsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('consultants'), link: '/consultants-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createConsultant = async (event) => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let fullname = event.target.consultantFullname.value.trim();
		if(!typeChecker(fullname, 'title'))
			return toast.warn(t('fullname') + ' ' + t('must_have_at_least_3_characters'));
		let is_active = event.target.ConsultantIsActive.checked ? 1 : 0;
		let vocation = event.target.consultantVocation.value.trim();
		if(!typeChecker(vocation, 'title'))
			return toast.warn(t('vocation') + ' ' + t('must_have_at_least_3_characters'));
		let image = document.querySelector('#consultantImage-upload-list img')?.getAttribute('src').trim();
		if(!typeChecker(image, 'image'))
			return toast.warn(t('image') + ' ' + t('cant_be_empty'));
		let onlineConsultationPrice = parseInt(toEnglishDigits(decommafy(event.target.consultantOnlinePrice.value.trim())));
		if(!typeChecker(onlineConsultationPrice, 'number'))
			return toast.warn(t('enter_online_consultation_price_correctly'));
		let offlineConsultationPrice = parseInt(toEnglishDigits(decommafy(event.target.consultantOfflinePrice.value.trim())));
		if(!typeChecker(offlineConsultationPrice, 'number'))
			return toast.warn(t('enter_face_to_face_consultation_price_correctly'));
		let description = event.target.consultantDescription.value.trim();
		if(!typeChecker(description, 'title'))
			return toast.warn(t('description') + ' ' + t('must_have_at_least_3_characters'));
		let body = {
			consultant_id: id,
			consultant_name: fullname,
			is_active,
			consultant_information: {
				consultant_title: vocation,
				consultant_icon: image,
				consultant_offline_price: onlineConsultationPrice,
				consultant_online_price: offlineConsultationPrice,
				consultant_description: description
			}
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/consultants/admin/' + componentType, body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const deleteConsultant = async () => {
		if(deleteButtonIsLoading)
			return 0;
		let body = {
			consultant_id: id
		};
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true); 
			let response = await _post('/consultants/admin/delete', body);
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					navigate("/consultants-list");
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [consultant, setConsultant] = useState({});
	const getConsultant = async () => {
		let body = {
			consultant_id: id
		};
		let response = await _post('/consultants/admin/getOne', body);
		switch(response.code){
			case 200:
				setConsultant(response.data.consultant);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		if(componentType === 'edit')
			getConsultant();
	}, []);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('consultant')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createConsultant}>
						<div className="row gx-2">
							<FormField fieldKey="consultantFullname" title={t('fullname')} value={consultant?.consultant_name} required rtl />
							<FormField type="toggle" fieldKey="ConsultantIsActive" title={t('active')} value={consultant?.is_active} required />
							<FormField type="text" fieldKey="consultantDescription" title={t('description')} value={consultant?.consultant_information?.consultant_description} required rtl />
							<FormField type="image" fieldKey="consultantImage" title={t('image')} value={consultant?.consultant_information?.consultant_icon.replace('https', 'http')} required tip="200*200" fileMaxSize={1} />
							<FormField type="number" fieldKey="consultantOnlinePrice" title={t('online_consultation_price')} tip={t('score')} value={commafy(consultant?.consultant_information?.consultant_online_price)} required ltr />
							<FormField type="number" fieldKey="consultantOfflinePrice" title={t('face_to_face_consultation_price')} tip={t('score')} value={commafy(consultant?.consultant_information?.consultant_offline_price)} required ltr />
							<FormField fieldKey="consultantVocation" title={t('vocation')} value={consultant?.consultant_information?.consultant_title} required rtl />
							<FormField fieldKey="consultantRate" title={t('rate')} value={consultant?.rate} disabled ltr />
						</div>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<>
									<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
										<i className="fa fa-save"></i> {t('update')}
									</LoadingButton>
									<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteConsultant} type="button" name="delete" isLoading={deleteButtonIsLoading}>
										<i className="fa fa-trash"></i> {t('delete')}
									</LoadingButton>
								</>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ConsultantsCreate;
